import DocumentLink from './documents/DocumentLink'
import { readerContextFromHref } from '../lib/documentReader'
/* One piece of evidence as ONE sentence (S3, 2026-09-03), shared by the
 * object drawer and the document page's "what this document proves" card:
 *
 *   N22.pdf, page 1, shows the company's existence — context only, being
 *   checked; valid from 3 Aug 2026 with no end date.
 *
 * The document, the page, the dates are DATA; what it shows, its weight
 * and its validity are the record's enums in officer words. The document
 * datum is a door to the document when a link is given; the page datum is
 * a door that re-jumps the viewer when a handler is given (the document
 * page's "what this document proves" card, 2026-09-03). */

import { Link } from 'react-router-dom'
import { compileSentence, tightBefore } from '../lib/sentences/compile'
import {
  purposeWords,
  validityWords,
  weightWords,
  windowMeaning,
  type EvidenceTargetType,
} from '../lib/sentences/words'
import { Part } from './Sentence'

export interface EvidenceLike {
  id: string
  document_id: string
  document_filename?: string | null
  evidential_purpose: string
  evidential_weight: string
  validity_status: string
  valid_from: string | null
  valid_to: string | null
  source_page: number | null
  source_text?: string | null
}

export default function EvidenceSentence({
  ev,
  target,
  nature,
  filename,
  href,
  linkTestId,
  onPage,
  pageTestId,
  'data-testid': testId,
}: {
  ev: EvidenceLike
  target: EvidenceTargetType
  nature?: string | null
  /** The document's name when the row does not carry it (the document page). */
  filename?: string | null
  href?: string
  linkTestId?: string
  /** Makes the page datum a door: called with the cited page. */
  onPage?: (page: number) => void
  pageTestId?: string
  'data-testid'?: string
}) {
  const page = ev.source_page
  const base = compileSentence('evidence.drawer.row', {
    document: ev.document_id && href ? 'Document' : (ev.document_filename ?? filename ?? null),
    page: ev.source_page,
    purpose: purposeWords(ev.evidential_purpose, target, nature),
    weight: weightWords(ev.evidential_weight),
    validity: validityWords(ev.validity_status),
  })
  const win = compileSentence(windowMeaning(ev), { from: ev.valid_from, to: ev.valid_to })
  return (
    <span
      className="sn"
      data-testid={testId}
      data-meaning="evidence.drawer.row"
      title={`${base.text}; ${win.text}`}
    >
      {base.parts.map((p, i) =>
        p.kind === 'datum' && p.slot === 'document' && href && ev.document_id ? (
          <DocumentLink
            key={i}
            document={{ document_id: ev.document_id }}
            context={readerContextFromHref(href)}
            className={`sn-datum drawer-evlink${tightBefore(base.parts, i) ? ' tight' : ''}`}
            data-slot="document"
            data-kind="name"
            data-testid={linkTestId}
          />
        ) : p.kind === 'datum' && p.slot === 'document' && href ? (
          <Link
            key={i}
            to={href}
            className={`sn-datum drawer-evlink${tightBefore(base.parts, i) ? ' tight' : ''}`}
            data-slot="document"
            data-kind="name"
            data-testid={linkTestId}
          >
            {p.text}
          </Link>
        ) : p.kind === 'datum' && p.slot === 'page' && onPage && page ? (
          <button
            key={i}
            type="button"
            className={`sn-datum sn-door${tightBefore(base.parts, i) ? ' tight' : ''}`}
            data-slot="page"
            data-kind="count"
            data-page={page}
            data-testid={pageTestId}
            title={`show page ${page}`}
            onClick={() => onPage(page)}
          >
            {p.text}
          </button>
        ) : (
          <Part p={p} key={i} tight={tightBefore(base.parts, i) || i === base.parts.length - 1} />
        )
      )}
      <span>; </span>
      {win.parts.map((p, i) => (
        <Part p={p} key={`w${i}`} tight={tightBefore(win.parts, i)} />
      ))}
    </span>
  )
}
